/* Interface categories on the VIDE off-site program.
 *
 * The GIS records, against each school, which kinds of interface are live
 * there. It records them packed into one field, like this:
 *
 *   SAF - SAFETY_SPEEDBUMP:1,2, INFRA_DRAINAGE:3,5,26, ACC - ACCESS_BUS:1
 *
 * which is a list of category codes, each followed by the reference numbers of
 * the items behind it. The codes are the ones in the team's Interface Control
 * Document. This file turns that field into something a screen can count.
 *
 * Where the GIS and the ICD disagree, the GIS wins. The ICD has not been
 * revised since the tagging started and the GIS is the copy people work in.
 * The visible case is SAFETY_SECURITY, which the GIS files under Safety and
 * the ICD does not define at all, having ACCESS_SECURITY under Access instead.
 * It is the third most used category on the program, so it is carried here
 * as the GIS writes it, with the ICD's wording noted against it.
 */

export const FAMILIES = [
  { code: "SAF", label: "Safety and life safety" },
  { code: "INF", label: "Infrastructure and utilities" },
  { code: "ACC", label: "Access and transportation" },
  { code: "SIT", label: "Site conditions" },
  { code: "REG", label: "Regulatory and compliance" },
];

export const FAMILY_LABEL = Object.fromEntries(FAMILIES.map((f) => [f.code, f.label]));

/* Plain wording from the ICD, so a chart reads "School bus turning radius"
   rather than ACCESS_BUS. */
export const CATEGORIES = {
  SAFETY_EGRESS: { family: "SAF", label: "Means of egress or ingress" },
  SAFETY_CONSTRUCTION: { family: "SAF", label: "Active construction hazards" },
  SAFETY_TRAFFIC: { family: "SAF", label: "High traffic areas" },
  SAFETY_CROSSWALK: { family: "SAF", label: "Crosswalks" },
  SAFETY_SPEEDBUMP: { family: "SAF", label: "Speed control" },
  SAFETY_HAZMAT: { family: "SAF", label: "Hazardous materials" },
  /* In the GIS under Safety. The ICD calls this ACCESS_SECURITY and files it
     under Access. Nobody has ruled on it, so it stays where the GIS puts it. */
  SAFETY_SECURITY: { family: "SAF", label: "Controlled access and security" },

  INFRA_DRAINAGE: { family: "INF", label: "Stormwater and drainage" },
  INFRA_GUT: { family: "INF", label: "Guts and drainage channels" },
  INFRA_UTILITIES: { family: "INF", label: "WAPA and electrical access" },
  INFRA_WASTEWATER: { family: "INF", label: "Wastewater treatment" },
  INFRA_WATER: { family: "INF", label: "Water runoff and springs" },
  INFRA_ROADS: { family: "INF", label: "Road stability and widening" },

  ACCESS_PARKING: { family: "ACC", label: "Parking and drop off" },
  ACCESS_BUS: { family: "ACC", label: "School bus access" },
  ACCESS_PEDESTRIAN: { family: "ACC", label: "Sidewalks and pedestrian access" },
  ACCESS_VEHICULAR: { family: "ACC", label: "Vehicle circulation" },
  ACCESS_SECURITY: { family: "ACC", label: "Controlled access requirements" },

  SITE_ACQUISITION: { family: "SIT", label: "Property acquisition or lease" },
  SITE_TOPOGRAPHY: { family: "SIT", label: "Slope and grade" },
  SITE_ENVIRONMENTAL: { family: "SIT", label: "Environmentally sensitive areas" },
  SITE_EXISTING: { family: "SIT", label: "Existing structures and conditions" },

  REG_EMINENT_DOMAIN: { family: "REG", label: "Eminent domain" },
  REG_ACOE: { family: "REG", label: "Army Corps of Engineers" },
  REG_FEDERAL: { family: "REG", label: "Federal land" },
  REG_HISTORIC: { family: "REG", label: "Historic preservation" },
  REG_PERMITTING: { family: "REG", label: "Special permitting" },
};

const CODES = Object.keys(CATEGORIES);

/* The GIS field has a length limit and a few schools run into it, so the last
   code on a long school arrives cut short: REG_PERMITTIN with its G missing
   and its numbers gone. Throwing that away would quietly lose a real tag off
   the schools carrying the most, which are exactly the ones worth counting.
   A stub is accepted only when it can mean one thing. */
function resolve(code) {
  if (CATEGORIES[code]) return code;
  const matches = CODES.filter((c) => c.startsWith(code));
  return matches.length === 1 ? matches[0] : null;
}

/* Every code in the packed field, with the reference numbers behind each.
 *
 * The references are left as written. Most are numbers, some carry a family of
 * their own ("inf 8", "acc34"), and what they point at has not been confirmed
 * by anybody, so nothing here treats them as meaning anything. They are shown
 * where a school is opened and counted nowhere.
 */
export function parseInterfaceTags(packed) {
  const text = String(packed ?? "").trim();
  if (!text) return [];

  /* Every code in the field, in order, with where each one sits. What follows
     a code, up to the next one, is its references. */
  const marks = [...text.matchAll(/([A-Z][A-Z_]{2,}):/g)];
  const found = [];
  const seen = new Map();

  /* A code can appear more than once in one school's field, each time with a
     different reference against it. That is one interface with several items
     behind it, not several interfaces, so the references gather onto the one
     entry. */
  const add = (raw, refs = []) => {
    const code = resolve(raw);
    if (!code) return;
    const already = seen.get(code);
    if (already) {
      for (const r of refs) if (!already.refs.includes(r)) already.refs.push(r);
      return;
    }
    const entry = { code, family: CATEGORIES[code].family, label: CATEGORIES[code].label, refs: [...refs] };
    seen.set(code, entry);
    found.push(entry);
  };

  for (let i = 0; i < marks.length; i += 1) {
    const from = marks[i].index + marks[i][0].length;
    const to = i + 1 < marks.length ? marks[i + 1].index : text.length;
    /* A family prefix sitting just before the next code, "SAF - ", belongs to
       that code and not to this one's references. */
    const blob = text.slice(from, to).replace(/[,;]?\s*[A-Z]{2,5}\s*-\s*$/, "");
    const refs = blob.split(/[,;]/).map((r) => r.trim()).filter(Boolean);

    /* The field has a length limit in the GIS and the schools carrying the
       most tags run into it, so the last code arrives cut short and lands in
       the previous code's references instead of on its own. */
    const trailing = refs.length
      ? /^(?:[A-Z]{2,5}\s*-\s*)?([A-Z][A-Z_]{2,})$/.exec(refs[refs.length - 1])
      : null;
    if (trailing) refs.pop();

    add(marks[i][1], refs);
    if (trailing && i === marks.length - 1) add(trailing[1]);
  }

  /* A field holding nothing but a cut off code, with no colon anywhere in it. */
  if (!marks.length) {
    const tail = /(?:[A-Z]{2,5}\s*-\s*)?([A-Z][A-Z_]{3,})\s*$/.exec(text);
    if (tail) add(tail[1]);
  }

  return found;
}

/* What the whole program is carrying, by category and by family.
 *
 * Counted in schools, not in tags: a school that has drainage tagged twice has
 * one drainage problem as far as this is concerned, and the number people ask
 * for is how many schools have a drainage problem.
 */
export function interfaceSummary(rows = [], field = "Risk") {
  const byCode = new Map();
  const byFamily = new Map(FAMILIES.map((f) => [f.code, { ...f, schools: [], categories: 0 }]));
  let tagged = 0;

  for (const row of rows) {
    const tags = parseInterfaceTags(row[field]);
    if (tags.length) tagged += 1;
    for (const t of tags) {
      if (!byCode.has(t.code)) {
        byCode.set(t.code, { ...CATEGORIES[t.code], code: t.code, schools: [] });
      }
      byCode.get(t.code).schools.push(row);
      const fam = byFamily.get(t.family);
      if (fam && !fam.schools.includes(row)) fam.schools.push(row);
    }
  }

  const categories = [...byCode.values()]
    .map((c) => ({ ...c, count: c.schools.length }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

  for (const c of categories) {
    const fam = byFamily.get(c.family);
    if (fam) fam.categories += 1;
  }

  const families = [...byFamily.values()]
    .map((f) => ({ ...f, count: f.schools.length }))
    .filter((f) => f.count > 0)
    .sort((a, b) => b.count - a.count);

  return { categories, families, tagged, untagged: rows.length - tagged };
}
